import { Button, Grid, Group, LoadingOverlay, Stack } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import React, { useEffect } from 'react';
import { z } from 'zod';

import { EnvironmentCountries } from '@e-origin/shared';

import { LeavePageNotification, PasswordInput, TextInput } from '@components';
import { CUSTOMS_SENDING_DELAY_MIN_SECONDS, CUSTOMS_SENDING_DELAY_SECONDS, DEFAULT_TRANSIT_PROCESS_ID_SEQUENCE_NUMBER } from '@constants';
import { useDeclarant, useUpdateDeclarant } from '@hooks/declarant';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';

const customsAuthSchema = z.object({
  clientId: z.string().min(1, { message: 'Required' }),
  password: z.string().min(1, { message: 'Required' }),
  systemName: z.string().min(1, { message: 'Required' }),
  enterpriseNumber: z.string().min(1, { message: 'Required' }),
  sendingDelaySeconds: z.any().refine((val) => val && Number(val) >= CUSTOMS_SENDING_DELAY_MIN_SECONDS, {
    message: 'Required',
  }),
});

interface CustomsAuthenticationFormProps {
  editDisabled: boolean;
}

export const CustomsAuthenticationForm: React.FC<CustomsAuthenticationFormProps> = ({ editDisabled }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();

  const updateDeclarantMutation = useUpdateDeclarant();

  const onSubmit = (values) => {
    updateDeclarantMutation.mutate({
      declarantId: declarant._id,
      declarant: {
        customsAuthentication: values,
      },
    });
  };

  const buildInitialValues = (dec) => ({
    clientId: dec?.customsAuthentication?.clientId ?? '',
    password: dec?.customsAuthentication?.password ?? '',
    systemName: dec?.customsAuthentication?.systemName ?? '',
    enterpriseNumber: dec?.customsAuthentication?.enterpriseNumber ?? '',
    secret: dec?.customsAuthentication?.secret ?? '',
    sendingDelaySeconds: dec?.customsAuthentication?.sendingDelaySeconds ?? CUSTOMS_SENDING_DELAY_SECONDS,
  });

  const form = useFormWithNameInputProp({
    initialValues: buildInitialValues(declarant),
    validate: zodResolver(customsAuthSchema),
    validateInputOnBlur: true,
  });

  useEffect(() => {
    form.setValues(buildInitialValues(declarant));
    form.resetDirty({} as any);
  }, [declarant]);

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} />

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="md">
          <Grid>
            <Grid.Col span={5}>
              <TextInput {...form.getInputProps('clientId')} label="Client id" />
            </Grid.Col>
            <Grid.Col span={3}>
              <PasswordInput {...form.getInputProps('password')} label="Password" />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={5}>
              <TextInput {...form.getInputProps('systemName')} label="System name" />
            </Grid.Col>
            <Grid.Col span={3}>
              <TextInput {...form.getInputProps('enterpriseNumber')} label="Enterprise number" />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={8}>
              <TextInput {...form.getInputProps('secret')} label="Secret" />
            </Grid.Col>
          </Grid>
          {settings?.country === EnvironmentCountries.NL && (
            <Grid>
              <Grid.Col span={3}>
                <TextInput
                  value={declarant.customsAuthentication?.transitProcessIdSequenceNumber ?? DEFAULT_TRANSIT_PROCESS_ID_SEQUENCE_NUMBER}
                  label="NCTS Process id sequence number"
                  disabled
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
        <Group py="xl">
          <Button disabled={editDisabled || !form.isValid()} type="submit" variant="filled" size="lg">
            Save
          </Button>
        </Group>
      </form>
    </>
  );
};
